<template>
  <div class="partner-container">
    <div class="partner-left-box">
      <StepsBar
        :steps="steps"
        :current-step="currentStep"
        @step-clicked="handleStepClick"
      />

      <!-- Step Content -->
      <div class="partner-step-content">
        <component
          :is="steps[currentStep - 1].component"
          :data="data"
          :ref="steps[currentStep - 1].component"
          class="partner-step-component"
          @update-partner-data="updatePartnerData"
        />
      </div>
    </div>
    <div class="partner-right-box">
      <div class="row">
        <span class="partner-right-box-title">{{
          $t("scale.weighing_on_scale.title_info_date")
        }}</span>
      </div>
      <div class="row">
        <div class="col-md-6">
          <div class="partner-right-box-label-content">
            <span>{{ $t("common.name") }}</span>
          </div>
        </div>
        <div class="col-md-6">
          <div class="partner-right-box-value-content">
            <span :style="{ color: data?.name ? 'inherit' : 'red' }">
              {{ data?.name || "*" }}
            </span>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <div class="partner-right-box-label-content">
            <span>{{ $t("stock_management.partners.legal_form") }}</span>
          </div>
        </div>
        <div class="col-md-6">
          <div class="partner-right-box-value-content">
            <span :style="{ color: data?.legal_form ? 'inherit' : 'red' }">
              {{ data?.legal_form || "*" }}
            </span>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <div class="partner-right-box-label-content">
            <span>{{ $t("stock_management.partners.id_number") }}</span>
          </div>
        </div>
        <div class="col-md-6">
          <div class="partner-right-box-value-content">
            <span :style="{ color: data?.id_number ? 'inherit' : 'red' }">
              {{ data?.id_number || "*" }}
            </span>
          </div>
        </div>
      </div>
      <div class="row">
        <AgroActionButtonsVertical
          :uuid="data?.uuid"
          :can-edit="canEdit"
          :can-delete="canDelete"
          @onSave="onSave"
          @onSaveAndNew="onSaveAndNew"
          :save-and-new="saveAndNew"
          @onDelete="onDelete"
          @onGotoList="onGotoList"
        >
        </AgroActionButtonsVertical>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapActions } from "vuex";
import AgroActionButtonsVertical from "@/components/AgroActionButtonsVertical/index";
import PartnerGeneralData from "~/pages/stock-management/sm-partners/partials/PartnerGeneralData.vue";
import PartnerContactPersons from "~/pages/stock-management/sm-partners/partials/PartnerContactPersons.vue";
import PartnerAddress from "~/pages/stock-management/sm-partners/partials/PartnerAddress.vue";
import PartnerBankingData from "~/pages/stock-management/sm-partners/partials/PartnerBankingData.vue";
import PartnerIdentificationData from "~/pages/stock-management/sm-partners/partials/PartnerIdentificationData.vue";
import StepsBar from "@/components/Common/StepsBar.vue";
import notify from "devextreme/ui/notify";

export default {
  name: "PartnerAddOrEditContent",
  components: {
    StepsBar,
    PartnerGeneralData,
    PartnerContactPersons,
    PartnerAddress,
    PartnerBankingData,
    PartnerIdentificationData,
    AgroActionButtonsVertical,
  },
  props: {
    partnerData: {
      type: Object,
      required: true,
    },
    canEdit: {
      type: Boolean,
      required: true,
    },
    canDelete: {
      type: Boolean,
      required: true,
    },
    saveAndNew: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      steps: [
        {
          number: 1,
          component: "PartnerGeneralData",
          label: this.$t("stock_management.partners.edit_groups.general_dat"),
        },
        {
          number: 2,
          component: "PartnerIdentificationData",
          label: this.$t(
            "stock_management.partners.edit_groups.identification_data"
          ),
        },
        {
          number: 3,
          component: "PartnerBankingData",
          label: this.$t("stock_management.partners.edit_groups.banking_data"),
        },
        {
          number: 4,
          component: "PartnerAddress",
          label: this.$t("common.address"),
        },
        {
          number: 5,
          component: "PartnerContactPersons",
          label: this.$t(
            "common.contact_person"
          ),
        },
      ],
      currentStep: 1, // Set the current active step
      data: this.partnerData,
      is_active: true,
    };
  },
  computed: {
    ...mapGetters({}),
  },
  methods: {
    ...mapActions({
      getPartnerData: "smPartners/getSmPartner",
    }),
    loadPartnerData() {
      if (!this.data.uuid) {
        return;
      }

      this.getPartnerData({
        uuid: this.data.uuid,
      }).then((response) => {
        if (response) {
          for (const step of this.steps) {
            const stepComponent = this.$refs[step.component];
            if (
              stepComponent &&
              typeof stepComponent.updateData === "function"
            ) {
              if (step.number === 1) {
                stepComponent.updateData(response);
              } else {
                setTimeout(() => {
                  stepComponent.updateData(response);
                }, step.number * 10);
              }
            }
          }
        }
      });
    },
    handleStepClick(stepNumber) {
      // Check if the step is valid
      if (stepNumber < 1 || stepNumber > this.steps.length) {
        return;
      }

      if (stepNumber < this.currentStep) {
        this.currentStep = stepNumber;
        return;
      }

      const currentStepRef =
        this.$refs[this.steps[this.currentStep - 1].component];
      const currentStepComponent = Array.isArray(currentStepRef)
        ? currentStepRef[0]
        : currentStepRef;
      if (
        currentStepComponent &&
        typeof currentStepComponent.validate === "function"
      ) {
        const isValid = currentStepComponent.validate();
        if (!isValid) {
          notify({
            message: this.$t("fill_out_error"),
            type: "error",
            displayTime: 20500,
            width: "auto",
            position: { at: "top center", my: "top center" },
          });
          return;
        }
      }

      this.currentStep = stepNumber;
    },
    updatePartnerData(updatedData) {
      this.data = { ...this.data, ...updatedData };
    },
    validate() {
      for (const step of this.steps) {
        const stepComponent = this.$refs[step.component];
        if (stepComponent && typeof stepComponent.validate === "function") {
          const isValid = stepComponent.validate();
          if (!isValid) {
            notify({
              message: this.$t("fill_out_error"),
              type: "error",
              displayTime: 20500,
              width: "auto",
              position: { at: "top center", my: "top center" },
            });
            return false;
          }
        }
      }

      return true;
    },
    reset(data) {
      this.data = data;
      this.currentStep = 1;
      for (const step of this.steps) {
        const stepComponent = this.$refs[step.component];
        if (stepComponent && typeof stepComponent.updateData === "function") {
          stepComponent.updateData(data);
        }
      }
    },
    onSave() {
      if (!this.validate()) {
        return;
      }
      this.$emit("onSavePartner", this.data);
    },
    onSaveAndNew() {
      if (!this.validate()) {
        return;
      }
      this.$emit("onSaveAndNewPartner", this.data);
    },
    onDelete() {
      this.$emit("onDeletePartner");
    },
    onGotoList() {
      this.$emit("onGotoListPartner");
    },
  },
  mounted() {
    setTimeout(() => {
      this.loadPartnerData();
    }, 500);
  },
};
</script>

<style scoped>
.partner-container {
  display: flex;
  height: 100%;
  width: 100%;
}

.partner-step-content {
  padding: 20px;
}

.partner-step-component {
  width: 100%;
  height: 100%;
  min-height: 450px;
}

/* Left Box */
.partner-left-box {
  flex: 1;
  background-color: #f9f9f9;
  display: flex;
  flex-direction: column;
}

/* Right Box */
.partner-right-box {
  width: 270px;
  padding: 10px;
  background-color: #f0f0f0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 15px;
  padding-left: 15px;
  padding-right: 5px;
  background-color: #f0f0f0;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  overflow-x: hidden;
}

.partner-right-box-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 15px;
  text-align: left;
}

.partner-right-box-label-content {
  font-size: 13px;
  padding-bottom: 5px;
}

.partner-right-box-value-content {
  font-size: 13px;
  font-weight: bold;
  padding-bottom: 5px;
}
</style>

<style>
</style>
